import { useRouter } from 'vue-router';
import { useAutopilotScope } from '@llmor/autopilot-vue';
import type { AutopilotFunctionContext, AutopilotFunctionInput } from '@/autopilot/functions';

/**
 * Binds a set of domain-specific autopilot functions to the calling component's lifecycle:
 * they are registered during setup and unregistered on unmount, so a function is only available
 * while the page/feature that owns it is active.
 *
 * Accepts ready definitions (page-local deps already bound) or factories that build one from
 * the app context (e.g. needing `router`).
 *
 * Register during setup (not onMounted) so a route-readiness `ready()` that fires on the next
 * tick still sees these tools. Registering before/without a relay connection is harmless — the
 * client registry persists and syncs on connect.
 *
 * Unmount only unregisters names this instance still owns (`useAutopilotScope`), so a keyed remount
 * of the same page keeps the new instance's functions.
 */
export function useAutopilotFunctions(functions: AutopilotFunctionInput[]) {
  const scope = useAutopilotScope();
  const router = useRouter();
  const ctx: AutopilotFunctionContext = { router };

  for (const fn of functions) {
    scope.registerFunction(typeof fn === 'function' ? fn(ctx) : fn);
  }
}
